import React from 'react';
import { Modal } from '../common/Modal';
import { Movement } from '../../types/movement';
import { Pilgrim } from '../../types/pilgrim';
import { Download } from 'lucide-react';
import { exportMovementDetails } from '../../services/movements/exportService';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  movement: Movement;
  pilgrims: Pilgrim[];
};

export const MovementDetailsModal = ({ isOpen, onClose, movement, pilgrims }: Props) => {
  const { translations } = useLanguage();

  const handleExport = async () => {
    try {
      exportMovementDetails(movement, pilgrims, translations);
      toast.success(translations?.movementDetails?.exportSuccess || 'Details exported successfully');
    } catch (error) {
      console.error('Error exporting details:', error);
      toast.error(translations?.movementDetails?.exportError || 'Failed to export details');
    }
  };

  const getLocationLabel = (city: string): string => {
    switch (city) {
      case 'makkah':
        return translations?.cities?.makkah || 'Makkah';
      case 'madinah':
        return translations?.cities?.madinah || 'Madinah';
      case 'jeddah':
        return translations?.cities?.jeddah || 'Jeddah';
      default:
        return city;
    }
  };

  const getGenderLabel = (gender: string): string => {
    return gender === 'male' 
      ? translations?.common?.male || 'Male'
      : translations?.common?.female || 'Female';
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={translations?.movementDetails?.title || 'Movement Details'}
    >
      <div className="space-y-6">
        {/* Movement Info */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">
              {translations?.movementDetails?.pilgrimsCount || 'Pilgrims Count'}:
            </span>
            <span className="font-medium">{pilgrims.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">
              {translations?.movementDetails?.date || 'Date'}:
            </span>
            <span className="font-medium">{movement.departureDate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">
              {translations?.movementDetails?.departureTime || 'Departure Time'}:
            </span>
            <span className="font-medium">{movement.departureTime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">
              {translations?.movementDetails?.arrivalTime || 'Estimated Arrival Time'}:
            </span>
            <span className="font-medium">{movement.arrivalTime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">
              {translations?.movementDetails?.from || 'From'}:
            </span>
            <span className="font-medium">
              {movement.from.name} ({getLocationLabel(movement.from.city)})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">
              {translations?.movementDetails?.to || 'To'}:
            </span>
            <span className="font-medium">
              {movement.to.name} ({getLocationLabel(movement.to.city)})
            </span>
          </div>
        </div>

        {/* Export Button */}
        <div className="flex justify-end">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg 
                     hover:bg-primary-600 transition-colors"
          >
            <Download size={18} />
            <span>{translations?.movementDetails?.exportButton || 'Export Details'}</span>
          </button>
        </div>

        {/* Pilgrims List */}
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementDetails?.pilgrim || 'Pilgrim'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementDetails?.passportNumber || 'Passport Number'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementDetails?.gender || 'Gender'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementDetails?.country || 'Country'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementDetails?.package || 'Package'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementDetails?.mobileNumber || 'Mobile Number'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pilgrims.map((pilgrim) => (
                <tr key={pilgrim.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{pilgrim.Name}</td>
                  <td className="px-6 py-4">{pilgrim.Passport_Number}</td>
                  <td className="px-6 py-4">{getGenderLabel(pilgrim.Gender)}</td>
                  <td className="px-6 py-4">{pilgrim.Residence_Country}</td>
                  <td className="px-6 py-4">{pilgrim.package_nickName}</td>
                  <td className="px-6 py-4">{pilgrim.Mobile_Number}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};